
import { Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TimeSelectProps {
  hours: string;
  minutes: string;
  onChange: (hours: string, minutes: string) => void;
}

export default function TimeSelect({ 
  hours, 
  minutes, 
  onChange 
}: TimeSelectProps) {
  // Convert 24-hour format to 12-hour format for display
  const hourIn12Format = parseInt(hours) % 12 || 12;
  const amPm = parseInt(hours) >= 12 ? 'PM' : 'AM';
  
  // Create 12-hour format options (1-12)
  const hourOptions = Array.from(
    { length: 12 }, 
    (_, i) => (i + 1).toString()
  );

  const minuteOptions = Array.from(
    { length: 12 }, 
    (_, i) => (i * 5).toString().padStart(2, '0')
  );

  const handleHourChange = (hour: string) => {
    // Convert 12-hour format to 24-hour format
    let hour24 = parseInt(hour);
    if (amPm === 'PM' && hour24 < 12) {
      hour24 += 12;
    } else if (amPm === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    onChange(hour24.toString().padStart(2, '0'), minutes);
  };

  const handleAmPmChange = (newAmPm: string) => {
    let hour24 = parseInt(hours);
    if (newAmPm === 'PM' && hour24 < 12) {
      hour24 += 12;
    } else if (newAmPm === 'AM' && hour24 >= 12) {
      hour24 -= 12;
    }
    onChange(hour24.toString().padStart(2, '0'), minutes);
  };

  return (
    <div className="flex gap-2 flex-1">
      <div className="flex-1">
        <Select value={hourIn12Format.toString()} onValueChange={handleHourChange}>
          <SelectTrigger className="flex items-center">
            <Clock size={16} className="mr-2 text-gray-500" />
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent>
            {hourOptions.map(h => (
              <SelectItem key={h} value={h}>{h}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1">
        <Select value={minutes} onValueChange={(value) => onChange(hours, value)}>
          <SelectTrigger>
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            {minuteOptions.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-20">
        <Select value={amPm} onValueChange={handleAmPmChange}>
          <SelectTrigger>
            <SelectValue placeholder="AM/PM" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
